import { Acabados } from '@types'
import { isKBRCatalog } from '@utils/catalog/kbrCatalog'
import { toVector3 } from '@utils/conversion/toVector3'
import { shouldHideKbrIntegratedZocaloMesh } from '@utils/sceneObjects/linked/integratedZocaloAlto'
import { Box3, Material, Matrix4, Mesh, Object3D, Vector3 } from 'three'

const ZOCALO_NAME_PATTERN = /zocalo/i
const PATAS_NAME_PATTERN = /patas?/i
const LATERAL_CATALOG_PREFIX = /^(LA|LB|LC|CC|CB|RA|RB|RC)/i

function findAcabadoForMaterial(materialName: string, acabados: Acabados[]): Acabados | undefined {
  let acabado = acabados.find((a) => a.zona === materialName)
  if (!acabado) {
    acabado = acabados.find((a) => {
      const cleanMaterialName = materialName.replace(/[0-9]/g, '').trim()
      const cleanZonaName = (a.zona || '').replace(/[0-9]/g, '').trim()
      return cleanMaterialName.includes(cleanZonaName) || cleanZonaName.includes(cleanMaterialName)
    })
  }
  return acabado
}

function getMeshMaterialName(mesh: Mesh): string {
  const mat = mesh.material
  if (Array.isArray(mat)) return (mat[0] as Material)?.name ?? ''
  return (mat as Material)?.name ?? ''
}

const _meshGeomBox = new Box3()

/** AABB de la geometría propia del mesh (sin hijos). expandByObject incluiría Mesh001_2. */
function expandByOwnGeometry(box: Box3, mesh: Mesh): void {
  const geom = mesh.geometry
  if (!geom) return
  if (!geom.boundingBox) geom.computeBoundingBox()
  const gb = geom.boundingBox
  if (!gb || gb.isEmpty()) return
  mesh.updateWorldMatrix(true, false)
  _meshGeomBox.copy(gb).applyMatrix4(mesh.matrixWorld)
  box.union(_meshGeomBox)
}

function boxSizeFromMeshes(root: Object3D, shouldInclude: (mesh: Mesh) => boolean): Vector3 | null {
  const box = new Box3()
  let hasMesh = false
  root.updateMatrixWorld(true)
  root.traverse((obj) => {
    if (!(obj as Mesh).isMesh) return
    const mesh = obj as Mesh
    if (!shouldInclude(mesh)) return
    hasMesh = true
    expandByOwnGeometry(box, mesh)
  })
  if (!hasMesh || box.isEmpty()) return null
  return box.getSize(toVector3())
}

export function getGLBSize(glb: Record<string, any>) {
  const boundingBox = new Box3().setFromObject(glb as Object3D)
  return boundingBox.getSize(toVector3())
}

/**
 * AABB Y en espacio local del root (mm). No incluye lift/scale del padre,
 * para asentar el mesh sobre el zócalo (GV*: origen a menudo en centro o techo).
 */
export function getObjectLocalAabbMm(
  root: Object3D | null | undefined,
): { minX: number; maxX: number; minY: number; maxY: number; minZ: number; maxZ: number } | null {
  if (!root) return null
  const box = new Box3()
  const inv = new Matrix4()
  root.updateWorldMatrix(true, true)
  inv.copy(root.matrixWorld).invert()
  let any = false
  root.traverse((obj) => {
    const mesh = obj as Mesh
    if (!mesh.isMesh || !mesh.geometry) return
    if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox()
    const gb = mesh.geometry.boundingBox
    if (!gb || gb.isEmpty()) return
    const b = gb.clone()
    mesh.updateWorldMatrix(true, false)
    b.applyMatrix4(mesh.matrixWorld)
    b.applyMatrix4(inv)
    if (!any) {
      box.copy(b)
      any = true
    } else {
      box.union(b)
    }
  })
  if (!any || box.isEmpty()) return null
  return {
    minX: Math.round(box.min.x * 1000),
    maxX: Math.round(box.max.x * 1000),
    minY: Math.round(box.min.y * 1000),
    maxY: Math.round(box.max.y * 1000),
    minZ: Math.round(box.min.z * 1000),
    maxZ: Math.round(box.max.z * 1000),
  }
}

export function getObjectLocalAabbYMm(
  root: Object3D | null | undefined,
): { minY: number; maxY: number } | null {
  const aabb = getObjectLocalAabbMm(root)
  if (!aabb) return null
  return { minY: aabb.minY, maxY: aabb.maxY }
}

export function getObjectLocalAabbMinYMm(root: Object3D | null | undefined): number | null {
  return getObjectLocalAabbYMm(root)?.minY ?? null
}

/** loadAsync → { scene }; a veces sceneGLB guarda el Object3D raíz. */
export function getGlbObjectRoot(glb: unknown): Object3D | null {
  if (!glb || typeof glb !== 'object') return null
  const rec = glb as { scene?: Object3D }
  const root = rec.scene ?? (glb as Object3D)
  return typeof root.updateWorldMatrix === 'function' ? root : null
}

export function getCatalogGlbAabbMm(
  sceneGLB: Map<string | number, { glb?: unknown }>,
  catalogID?: string | number | null,
  extraKeys?: (string | number | null | undefined)[],
): ReturnType<typeof getObjectLocalAabbMm> {
  const keys: (string | number)[] = []
  if (catalogID != null && catalogID !== '') {
    keys.push(catalogID, String(catalogID))
  }
  for (const k of extraKeys ?? []) {
    if (k != null && k !== '') keys.push(k)
  }
  for (const k of keys) {
    const aabb = getObjectLocalAabbMm(getGlbObjectRoot(sceneGLB.get(k)?.glb))
    if (aabb) return aabb
  }
  return null
}

export function getGLBSizeExcludingMeshNames(
  root: Object3D,
  excludePattern: RegExp = ZOCALO_NAME_PATTERN,
): Vector3 | null {
  return boxSizeFromMeshes(root, (mesh) => !excludePattern.test(mesh.name))
}

export function getGLBSizeExcludingAcabadoZones(
  root: Object3D,
  acabados: Acabados[],
  excludeZones: string[],
): Vector3 | null {
  const exclude = new Set(excludeZones.map((z) => z.toLowerCase()))
  return boxSizeFromMeshes(root, (mesh) => {
    const acabado = findAcabadoForMaterial(getMeshMaterialName(mesh), acabados)
    if (!acabado) return true
    const zone = (acabado.zona || acabado.descripcion || '').toLowerCase()
    return !exclude.has(zone)
  })
}

export function getGLBSizeIncludingAcabadoZones(
  root: Object3D,
  acabados: Acabados[],
  includeZones: string[],
): Vector3 | null {
  const include = new Set(includeZones.map((z) => z.toLowerCase()))
  return boxSizeFromMeshes(root, (mesh) => {
    const acabado = findAcabadoForMaterial(getMeshMaterialName(mesh), acabados)
    if (!acabado) return false
    const zone = (acabado.zona || acabado.descripcion || '').toLowerCase()
    return include.has(zone)
  })
}

/**
 * KBR rinconero: la losa en L (Mesh001_2) y las patas no se llaman "zocalo",
 * así que el AABB completo sale ~+100 mm por eje (p.ej. 1100×900×820 en vez de
 * 1000×800×720). Misma exclusión que useGLBInstance al ocultar esos meshes.
 */
function isKbrFreeDimensionExcludedMesh(mesh: Mesh): boolean {
  if (shouldHideKbrIntegratedZocaloMesh(mesh)) return true
  const materialName = getMeshMaterialName(mesh)
  return PATAS_NAME_PATTERN.test(mesh.name) || PATAS_NAME_PATTERN.test(materialName)
}

/** Base de escala: mide Costado (laterales) o excluye Zócalo, no bbox completo con zócalo. */
export function getGLBSizeForFreeDimension(
  fullRoot: Object3D,
  fullSize: Vector3,
  acabados?: Acabados[],
  catalogID?: string | number,
): Vector3 {
  const code = catalogID != null ? String(catalogID).trim().toUpperCase() : ''
  const isLateral = LATERAL_CATALOG_PREFIX.test(code)

  if (acabados?.length && isLateral) {
    const costadoOnly = getGLBSizeIncludingAcabadoZones(fullRoot, acabados, ['Costado'])
    if (costadoOnly) return costadoOnly
  }

  if (isKBRCatalog(catalogID)) {
    const withoutKbrExtras = boxSizeFromMeshes(
      fullRoot,
      (mesh) => !isKbrFreeDimensionExcludedMesh(mesh),
    )
    if (withoutKbrExtras) return withoutKbrExtras
  }

  if (acabados?.length) {
    const withoutZocaloZone = getGLBSizeExcludingAcabadoZones(fullRoot, acabados, ['Zocalo'])
    if (withoutZocaloZone) return withoutZocaloZone
  }

  const withoutZocaloName = getGLBSizeExcludingMeshNames(fullRoot)
  if (withoutZocaloName) return withoutZocaloName

  return fullSize
}
